import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeroSection from "../components/HeroSection";
import { get, BASE_URL } from "../api/config"; // Import BASE_URL

const CompletedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompletedProjects();
  }, []);

  const fetchCompletedProjects = async () => {
    try {
      setLoading(true);
      const response = await get("public/projects?status_id=3");

      if (response.data && Array.isArray(response.data)) {
        setProjects(response.data);
      } else {
        setProjects([]);
      }
    } catch (err) {
      setError("Failed to fetch completed projects");
      console.error("Error fetching projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectClick = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  if (loading) {
    return (
      <div className="w-full bg-white py-12 px-4">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 inline-block relative pb-2">
              Completed Projects
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-600"></div>
            </h2>
          </div>
          <div className="text-center py-8">
            <div className="flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
              <span className="ml-4 text-lg text-gray-600">
                Loading completed projects...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white py-12 px-4">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 inline-block relative pb-2">
              Completed Projects
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-600"></div>
            </h2>
          </div>
          <div className="text-center py-8">
            <div className="text-red-600 mb-4 text-lg">{error}</div>
            <button
              onClick={fetchCompletedProjects}
              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors duration-300"
            >
              Retry Loading
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white py-12 px-4">
      <HeroSection />

      <div className="mx-auto max-w-6xl px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-green-600 inline-block relative pb-2">
            Completed Projects
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-600"></div>
          </h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
            Successfully delivered infrastructure projects that have made a
            positive impact in our community.
          </p>
          <div className="mt-4 text-gray-500">
            Total: {projects.length} project{projects.length !== 1 ? "s" : ""}{" "}
            completed
          </div>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="text-6xl mb-4">🏗️</div>
            <div className="text-gray-700 text-xl mb-2">
              No completed projects yet
            </div>
            <p className="text-gray-500 max-w-md mx-auto">
              All successfully delivered projects will appear here once they are
              marked as completed.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {projects.map((project) => (
                <ProjectCard
                  key={project.project_id}
                  project={project}
                  onProjectClick={handleProjectClick}
                />
              ))}
            </div>

            <div className="text-center bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg">
              <div className="text-gray-700 mb-4 text-lg">
                Successfully completed{" "}
                <span className="font-bold text-green-600">
                  {projects.length}
                </span>{" "}
                projects
              </div>
              <div className="text-gray-600 mb-4">
                Total investment: ₹{calculateTotalInvestment(projects)} crores
              </div>
              <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-green-600 hover:text-green-700 font-semibold text-lg underline transition-colors">
                View All Completed Projects →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ProjectCard = ({ project, onProjectClick }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatCost = (cost) => {
    if (!cost) return "₹ 0";
    const amount = parseFloat(cost);
    if (amount >= 10000000) {
      return `₹ ${(amount / 10000000).toFixed(1)} Cr`;
    } else if (amount >= 100000) {
      return `₹ ${(amount / 100000).toFixed(1)} L`;
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getProjectImage = (project) => {
    // First priority: Check project images array
    if (project.images && project.images.length > 0) {
      const imagePath = project.images[0].image_url;
      const baseUrl = BASE_URL.replace("/api/", "/");
      return baseUrl + imagePath.replace(/^\//, "");
    }

    // Second priority: Check timeline images
    if (project.timelines && project.timelines.length > 0) {
      const timeline = project.timelines[0];
      if (timeline.images && timeline.images.length > 0) {
        const imagePath = timeline.images[0].image_path;
        const baseUrl = BASE_URL.replace("/api/", "/");
        return baseUrl + imagePath;
      }
    }

    // Fallback based on project type
    const projectName = project.project_name?.toLowerCase() || "";
    if (projectName.includes("drain") || projectName.includes("water")) {
      return "https://images.unsplash.com/photo-1581094794329-c8112c4e5190?w=400&h=300&fit=crop";
    } else if (
      projectName.includes("road") ||
      projectName.includes("highway")
    ) {
      return "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=400&h=300&fit=crop";
    } else if (
      projectName.includes("khel") ||
      projectName.includes("stadium") ||
      projectName.includes("arena") ||
      projectName.includes("wrestling") ||
      projectName.includes("sports")
    ) {
      return "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&h=300&fit=crop";
    } else if (
      projectName.includes("school") ||
      projectName.includes("college") ||
      projectName.includes("education")
    ) {
      return "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=400&h=300&fit=crop";
    } else if (
      projectName.includes("hospital") ||
      projectName.includes("health")
    ) {
      return "https://images.unsplash.com/photo-1516549655669-df6654e435de?w=400&h=300&fit=crop";
    } else {
      return "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=300&fit=crop";
    }
  };

  const handleClick = () => {
    onProjectClick(project.project_id);
  };

  // Get number of images available for this project
  const getImageCount = () => {
    let count = 0;
    if (project.images) count += project.images.length;
    if (project.timelines) {
      project.timelines.forEach((timeline) => {
        if (timeline.images) count += timeline.images.length;
      });
    }
    return count;
  };

  const imageCount = getImageCount();

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      onClick={handleClick}
    >
      <div className="relative h-48">
        <img
          src={getProjectImage(project)}
          alt={project.project_name}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          onError={(e) => {
            // Fallback to category-based images if API image fails
            const projectName = project.project_name?.toLowerCase() || "";
            if (
              projectName.includes("drain") ||
              projectName.includes("water")
            ) {
              e.target.src =
                "https://images.unsplash.com/photo-1581094794329-c8112c4e5190?w=400&h=300&fit=crop";
            } else if (
              projectName.includes("road") ||
              projectName.includes("highway")
            ) {
              e.target.src =
                "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=400&h=300&fit=crop";
            } else if (
              projectName.includes("khel") ||
              projectName.includes("stadium") ||
              projectName.includes("arena") ||
              projectName.includes("wrestling") ||
              projectName.includes("sports")
            ) {
              e.target.src =
                "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&h=300&fit=crop";
            } else if (
              projectName.includes("school") ||
              projectName.includes("college") ||
              projectName.includes("education")
            ) {
              e.target.src =
                "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=400&h=300&fit=crop";
            } else if (
              projectName.includes("hospital") ||
              projectName.includes("health")
            ) {
              e.target.src =
                "https://images.unsplash.com/photo-1516549655669-df6654e435de?w=400&h=300&fit=crop";
            } else {
              e.target.src =
                "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=300&fit=crop";
            }
          }}
        />
        <div className="absolute top-3 left-3">
          <span className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
            ✓ Completed
          </span>
        </div>
        {imageCount > 0 && (
          <div className="absolute top-3 right-3">
            <span className="bg-black/70 text-white px-2 py-1 rounded text-xs">
              {imageCount} photo{imageCount !== 1 ? "s" : ""}
            </span>
          </div>
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-bold text-gray-900 text-xl leading-tight">
            {project.project_name}
          </h3>
          <div className="bg-green-100 text-green-800 p-1.5 rounded-full">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2 h-12">
          {enhanceDescription(project.description, project.project_name)}
        </p>

        <div className="space-y-3 text-sm text-gray-600 mb-4">
          <div className="flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-green-500 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="truncate">{project.location}</span>
          </div>

          {project.length_size && (
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-2 text-green-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                />
              </svg>
              <span>{project.length_size}</span>
            </div>
          )}

          {project.estimated_cost && (
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-2 text-green-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                />
              </svg>
              <span className="font-semibold text-gray-800">
                {formatCost(project.estimated_cost)}
              </span>
            </div>
          )}

          {project.end_date && (
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-2 text-green-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>Completed {formatDate(project.end_date)}</span>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <span className="text-gray-500 text-sm mr-2">Progress</span>
              <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-2 bg-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${project.progress || 100}%` }}
                ></div>
              </div>
            </div>
            <div className="text-green-600 font-semibold">
              {project.progress || 100}%
            </div>
          </div>
        </div>

        {/* View Details Button */}
        <button
          className="mt-4 w-full bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors duration-300"
          onClick={handleClick}
        >
          View Project Details
        </button>
      </div>
    </div>
  );
};

// Helper function to calculate total investment
const calculateTotalInvestment = (projects) => {
  const total = projects.reduce((sum, project) => {
    return sum + (parseFloat(project.estimated_cost) || 0);
  }, 0);
  return (total / 10000000).toFixed(2);
};

const enhanceDescription = (description, projectName) => {
  if (!description) return "A well-executed infrastructure project that has successfully improved community facilities.";
  
  // If description is already good, return as is
  if (description.length > 100) return description;
  
  // Enhance short descriptions based on project type
  const name = projectName?.toLowerCase() || "";
  
  if (name.includes("stadium") || name.includes("ground") || name.includes("khel") || name.includes("sports")) {
    return `${description} This world-class sports facility has been developed to provide excellent training and competitive opportunities for athletes of all levels.`;
  } else if (name.includes("road")) {
    return `${description} This infrastructure project has significantly improved connectivity and transportation efficiency in the region.`;
  } else if (name.includes("drain") || name.includes("water")) {
    return `${description} This drainage and water management system has enhanced sanitation and reduced waterlogging issues in the area.`;
  } else if (name.includes("school") || name.includes("education")) {
    return `${description} This educational facility has been constructed to provide quality learning infrastructure for students.`;
  } else if (name.includes("health") || name.includes("hospital")) {
    return `${description} This health facility has been established to provide accessible healthcare services to the community.`;
  }
  
  return `${description} This project represents a significant contribution to community development and infrastructure improvement.`;
};

export default CompletedProjects;
